/**
 * Transport-level reporting of failed Supabase calls (BAL-1144).
 *
 * Every Supabase client passes its `fetch` through this module, so a failed
 * database call is observed without any per-service code. The Result pattern
 * turns a failed query into a value; before this, a page could render an
 * error while Sentry held nothing (`/admin/artists`, 2026-10-05).
 *
 * This package cannot import Sentry or the error classifier: `@kit/shared`
 * already imports `@kit/supabase`, so the reverse import is a cycle. The
 * transport hands EVERY status >= 400 and every non-abort rejection to an
 * injected reporter, and the reporter (`@kit/shared/lib/db-failure-to-sentry`,
 * registered in `apps/web/instrumentation*.ts`) decides what is reportable.
 *
 * Two guarantees, both pinned in `__tests__/db-failure-reporter.test.ts`:
 *  - the caller receives exactly what the underlying fetch produced (same
 *    response, same error object, body still readable), whatever the reporter
 *    does;
 *  - the caller never waits on the report: the reporter runs on a later
 *    macrotask, and at most 64 KiB of an error body is read for it.
 */

export interface DbFailure {
  /**
   * `stalled`: our deadline fired, but more than `STALL_SLACK_MS` after the
   * budget it was set for. A timer that late proves the process was not
   * running when the deadline came due (the instance was suspended, or the
   * event loop was blocked), so nothing measured the database. Production,
   * 2026-10-10: background `unstable_cache` revalidations showed Supabase
   * fetch spans of 114 s to 615 s against a 3 s budget, while the same
   * statements never exceeded 600 ms in `pg_stat_statements`.
   */
  kind: 'timeout' | 'stalled' | 'network' | 'http';
  /**
   * `/rest/v1/<table>` gives `<table>`; `/rest/v1/rpc/<fn>` gives `rpc/<fn>`;
   * `/auth/v1/...` gives `auth`; `/storage/v1/...` gives `storage`;
   * `/functions/v1/...` gives `functions`; `/realtime/v1/...` gives
   * `realtime`; anything else gives `other`. Never the query string, never a
   * further path segment.
   */
  target: string;
  method: string;
  status: number | null;
  /** PostgREST / Postgres error code from the JSON body, when there is one. */
  code: string | null;
  message: string | null;
  queryClass: string;
  /**
   * Wall time from the call to its rejection. Present on every `TimeoutError`
   * (`timeout` or `stalled`), with or without a deadline budget; absent on
   * `http` and `network` failures.
   */
  elapsedMs?: number;
}

/**
 * How late a deadline may fire and still count as the database not answering.
 * A healthy timer fires within milliseconds of its due time; a full second
 * late means the timer itself could not run.
 */
export const STALL_SLACK_MS = 1000;

export type DbFailureReporter = (failure: DbFailure) => void;

/**
 * Next bundles `instrumentation.ts` apart from route code, so a module-level
 * variable set at registration can be a different instance from the one a
 * request reads. A `Symbol.for` key on `globalThis` is shared by every bundle
 * in the process.
 */
const REPORTER_KEY = Symbol.for('ballee.dbFailureReporter');

/** Error bodies are a few hundred bytes; nothing past this is read. */
const MAX_BODY_BYTES = 65536;

/**
 * Keeps the process alive until a deferred report has run. On Vercel a
 * function may freeze as soon as its handler returns; the server registers
 * `waitUntil` from `@vercel/functions` here so a report scheduled during a
 * request still runs. Stored under its own key so a bare reporter function
 * written to `REPORTER_KEY` by another bundle keeps working.
 */
export type DbFailureKeepAlive = (task: Promise<void>) => void;

const KEEP_ALIVE_KEY = Symbol.for('ballee.dbFailureKeepAlive');

type ReporterHolder = {
  [REPORTER_KEY]?: DbFailureReporter | null;
  [KEEP_ALIVE_KEY]?: DbFailureKeepAlive | null;
};

export function setDbFailureReporter(
  reporter: DbFailureReporter | null,
  options: { keepAlive?: DbFailureKeepAlive } = {},
): void {
  const holder = globalThis as ReporterHolder;
  holder[REPORTER_KEY] = reporter;
  holder[KEEP_ALIVE_KEY] = reporter ? (options.keepAlive ?? null) : null;
}

/**
 * Calls the reporter. Returns its promise, with any rejection swallowed, when
 * the reporter is async, so a deferred task (and the keep-alive holding it)
 * ends only after the report has.
 */
function report(failure: DbFailure): Promise<void> | void {
  try {
    const reporter = (globalThis as ReporterHolder)[REPORTER_KEY];
    if (typeof reporter !== 'function') return;

    // A `void`-typed callback may still be an async function; its rejection
    // must not surface as an unhandled rejection.
    const result: unknown = reporter(failure);
    if (
      typeof result === 'object' &&
      result !== null &&
      typeof (result as PromiseLike<unknown>).then === 'function'
    ) {
      return Promise.resolve(result).then(
        () => {},
        () => {},
      );
    }
  } catch {
    // Observability must never change what the caller receives.
  }
}

/**
 * Runs `task` on a later macrotask, so the caller has its response or
 * rejection (and postgrest-js has made its retry decision) before any report
 * work runs. The pending task is handed to the registered keep-alive NOW,
 * while the request is still in flight.
 */
function defer(task: () => void | Promise<void>): void {
  const pending = new Promise<void>((resolve) => {
    setTimeout(() => {
      Promise.resolve()
        .then(task)
        .catch(() => {})
        .finally(resolve);
    }, 0);
  });

  try {
    (globalThis as ReporterHolder)[KEEP_ALIVE_KEY]?.(pending);
  } catch {
    // A keep-alive that refuses still leaves the timer to run.
  }
}

function reportLater(failure: DbFailure): void {
  defer(() => report(failure));
}

const SERVICE_LABELS: Record<string, string> = {
  auth: 'auth',
  storage: 'storage',
  functions: 'functions',
  realtime: 'realtime',
};

function requestUrl(input: RequestInfo | URL): URL | null {
  try {
    if (input instanceof URL) return input;
    if (typeof input === 'string') return new URL(input);
    return new URL(input.url);
  } catch {
    return null;
  }
}

function targetOf(url: URL | null): { target: string; postgrest: boolean } {
  if (!url) return { target: 'other', postgrest: false };

  const [, service, version, first, second] = url.pathname.split('/');

  if (service === 'rest' && version === 'v1' && first) {
    const target = first === 'rpc' && second ? `rpc/${second}` : first;
    return { target: decodeSegment(target), postgrest: true };
  }

  if (version === 'v1' && service && service in SERVICE_LABELS) {
    return { target: SERVICE_LABELS[service]!, postgrest: false };
  }

  return { target: 'other', postgrest: false };
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function methodOf(input: RequestInfo | URL, init: RequestInit | undefined) {
  const fromInit = init?.method;
  if (fromInit) return fromInit.toUpperCase();
  if (typeof input === 'object' && 'method' in input && input.method) {
    return input.method.toUpperCase();
  }
  return 'GET';
}

function errorName(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const name = (error as { name?: unknown }).name;
  return typeof name === 'string' ? name : undefined;
}

function errorMessage(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' ? message : null;
}

/**
 * Reads the cloned response's body in the background, at most `MAX_BODY_BYTES`, and
 * reports with the PostgREST `code` and `message` when the body parses.
 *
 * The cap is strict on a byte stream (every `fetch` response body in Node and
 * current browsers): a BYOB reader is never handed more than the bytes still
 * allowed. A plain stream cannot be read in bounded pieces, so there one chunk
 * may arrive whole; nothing past the cap is retained and reading stops.
 */
async function reportFromBody(clonedResponse: Response, failure: DbFailure) {
  try {
    if (!clonedResponse.body) {
      await report(failure);
      return;
    }

    const bytes = await readCapped(clonedResponse.body);

    await report(
      bytes === null
        ? failure
        : { ...failure, ...parseError(new TextDecoder().decode(bytes)) },
    );
  } catch {
    await report(failure);
  }
}

/** The body, or `null` when it reached the cap (and was cancelled). */
async function readCapped(
  body: ReadableStream<Uint8Array>,
): Promise<Uint8Array | null> {
  let byob: ReadableStreamBYOBReader | undefined;
  try {
    byob = body.getReader({ mode: 'byob' });
  } catch {
    byob = undefined;
  }

  const buffer = new Uint8Array(MAX_BODY_BYTES);
  let total = 0;

  if (byob) {
    for (;;) {
      if (total >= MAX_BODY_BYTES) return cancelled(byob);
      const { done, value } = await byob.read(
        new Uint8Array(MAX_BODY_BYTES - total),
      );
      if (value) {
        buffer.set(value, total);
        total += value.byteLength;
      }
      if (done) return buffer.subarray(0, total);
    }
  }

  const reader = body.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return buffer.subarray(0, total);
    if (total + value.byteLength >= MAX_BODY_BYTES) return cancelled(reader);
    buffer.set(value, total);
    total += value.byteLength;
  }
}

/**
 * Cancels without awaiting: on a tee'd clone the cancel promise settles only
 * once the caller has read or cancelled the original body (measured on Node
 * 24.15), so awaiting it would delay the report until then, or forever.
 */
function cancelled(
  reader: ReadableStreamBYOBReader | ReadableStreamDefaultReader<Uint8Array>,
): null {
  void reader.cancel().catch(() => {});
  return null;
}

function parseError(text: string): Pick<DbFailure, 'code' | 'message'> {
  try {
    const body = JSON.parse(text) as { code?: unknown; message?: unknown };
    return {
      code: typeof body?.code === 'string' ? body.code : null,
      message: typeof body?.message === 'string' ? body.message : null,
    };
  } catch {
    return { code: null, message: null };
  }
}

/** Monotonic where available; never throws on a runtime without `performance`. */
function nowMs(): number {
  return globalThis.performance?.now() ?? Date.now();
}

/**
 * `timeout` when the deadline fired on time, `stalled` when it fired more than
 * `STALL_SLACK_MS` after `budgetMs`. Without a budget there is nothing to be
 * late against, so the rejection stays a `timeout`.
 */
function timeoutKind(
  elapsedMs: number,
  budgetMs: number | undefined,
): 'timeout' | 'stalled' {
  if (budgetMs === undefined) return 'timeout';
  return elapsedMs > budgetMs + STALL_SLACK_MS ? 'stalled' : 'timeout';
}

/**
 * Runs `inner(input, init)` and reports its failure. `queryClass` is the
 * class of THIS call; `budgetMs` is the deadline `inner` enforces, when it
 * enforces one (`makeTimeoutFetch` passes it).
 */
export async function fetchWithDbFailureReporting(
  inner: typeof fetch,
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  queryClass: string,
  budgetMs?: number,
): Promise<Response> {
  let response: Response;
  const startedAt = nowMs();

  try {
    response = await inner(input, init);
  } catch (error) {
    const name = errorName(error);

    // The caller cancelled (navigation, an explicit signal): not a failure.
    if (name !== 'AbortError') {
      const isTimeout = name === 'TimeoutError';
      const elapsedMs = Math.round(nowMs() - startedAt);
      reportLater({
        kind: isTimeout ? timeoutKind(elapsedMs, budgetMs) : 'network',
        target: targetOf(requestUrl(input)).target,
        method: methodOf(input, init),
        status: null,
        code: null,
        message: errorMessage(error),
        queryClass,
        ...(isTimeout ? { elapsedMs } : {}),
      });
    }

    throw error;
  }

  if (response.status < 400) return response;

  try {
    const { target, postgrest } = targetOf(requestUrl(input));
    const failure: DbFailure = {
      kind: 'http',
      target,
      method: methodOf(input, init),
      status: response.status,
      code: null,
      message: null,
      queryClass,
    };

    if (postgrest) {
      let clonedResponse: Response | undefined;
      try {
        // Clone synchronously, before the caller can consume the body.
        clonedResponse = response.clone();
      } catch {
        // Body already used or locked by an inner wrapper: the status is
        // still known, so report without a code.
        clonedResponse = undefined;
      }

      if (clonedResponse) {
        const body = clonedResponse;
        defer(() => reportFromBody(body, failure));
      } else {
        reportLater(failure);
      }
    } else {
      reportLater(failure);
    }
  } catch {
    // Building the observation must never change the caller's response.
  }

  return response;
}

/** A `fetch` with a fixed class, for clients that have no per-call class (the browser). */
export function withDbFailureReporting(
  inner: typeof fetch,
  queryClass: string,
): typeof fetch {
  return (input, init) =>
    fetchWithDbFailureReporting(inner, input, init, queryClass);
}
